import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import appStyle from '../../style.less'
import style from './style.less'
import Breadcrumbs from '@yaak/components/src/Breadcrumbs'
import Typography, {
  TypographySizes,
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Button from '@yaak/components/src/Button'
import Switch from '@yaak/components/src/Switch'
import SearchBar from '@yaak/components/src/SearchBar/SearchBar'
import {
  getPartners,
  getSimModuleAllowlist,
  getSimModuleCatalog,
  updateSimModuleAllowlist,
  SimModuleCatalogEntry,
} from '@yaak/components/services/api/api'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import {
  allowlistPartnerIds,
  hasAllowlistChanges,
} from '../../helpers/moduleConfig'

interface PartnerRow {
  id: string
  name: string
  simEnabled: boolean
  whitelisted: boolean
}

interface CustomModulePartnersPageProps {
  token: string
}

const CustomModulePartnersPage: React.FunctionComponent<
  CustomModulePartnersPageProps
> = ({ token }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [module, setModule] = useState<SimModuleCatalogEntry | null>(null)
  const [originalPartnerIds, setOriginalPartnerIds] = useState<string[]>([])
  const [partners, setPartners] = useState<PartnerRow[]>([])
  const [allowlistLoaded, setAllowlistLoaded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const { moduleId } = useParams()
  const { setShowToast } = useContext(ToastContext) as ToastContextType

  useEffect(() => {
    const fetchData = async () => {
      if (!moduleId) return

      setLoading(true)
      setAllowlistLoaded(false)

      const catalog = await getSimModuleCatalog({
        token,
        onAlert: setShowToast,
      })
      const matched = catalog?.modules?.find(
        (entry) => entry.moduleId === moduleId
      )
      setModule(matched || null)

      if (!matched?.isCustom) {
        setLoading(false)
        return
      }

      const [partnerList, allowlist] = await Promise.all([
        getPartners({ token, onAlert: setShowToast }),
        getSimModuleAllowlist({ token, moduleId, onAlert: setShowToast }),
      ])

      if (!Array.isArray(allowlist?.partnerIds)) {
        setLoading(false)
        return
      }

      const allowed = new Set(allowlist.partnerIds)
      setAllowlistLoaded(true)
      setOriginalPartnerIds(allowlist.partnerIds)
      setPartners(
        (partnerList || []).map((partner) => ({
          id: partner.id,
          name: partner.name,
          simEnabled: partner.simEnabled,
          whitelisted: allowed.has(partner.id),
        }))
      )
      setLoading(false)
    }

    token && fetchData()
  }, [token, moduleId])

  const togglePartner = (partnerId: string) => {
    setPartners((prev) =>
      prev.map((partner) =>
        partner.id === partnerId
          ? { ...partner, whitelisted: !partner.whitelisted }
          : partner
      )
    )
  }

  const partnerIds = allowlistPartnerIds(
    partners.map((partner) => partner.id),
    originalPartnerIds,
    partners
      .filter((partner) => partner.whitelisted)
      .map((partner) => partner.id)
  )
  const hasChanges = hasAllowlistChanges(partnerIds, originalPartnerIds)

  const save = async () => {
    if (!moduleId) return

    setSaving(true)
    const result = await updateSimModuleAllowlist({
      token,
      moduleId,
      partnerIds,
      onAlert: setShowToast,
    })

    if (result?.partnerIds) {
      const allowed = new Set(result.partnerIds)
      setOriginalPartnerIds(result.partnerIds)
      setPartners((prev) =>
        prev.map((partner) => ({
          ...partner,
          whitelisted: allowed.has(partner.id),
        }))
      )
    }

    setSaving(false)
  }

  const visiblePartners = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return partners
    return partners.filter(
      (partner) =>
        partner.name.toLowerCase().includes(query) ||
        partner.id.toLowerCase().includes(query)
    )
  }, [partners, searchQuery])

  return (
    <div className={appStyle.page}>
      <div className={style.container}>
        <Breadcrumbs
          first={{
            text: 'Custom Modules',
            url: '/simulator/custom-modules',
          }}
          second={{
            text: module?.title || moduleId,
          }}
          selfLink={true}
        />
        <div className={style.header}>
          <Typography
            type={TypographyTypes.headline}
            size={TypographySizes.small}
          >
            {module?.title || 'Loading...'}
          </Typography>
          <Button
            onClick={save}
            text={saving ? 'Saving...' : 'Save'}
            disabled={!hasChanges || saving || !allowlistLoaded}
          />
        </div>
        <Divider />
      </div>
      {loading ? (
        <ProgressBar />
      ) : !module?.isCustom ? (
        <div className={style.emptyState}>
          <Typography type={TypographyTypes.body}>
            {'This module is not custom. Mark it as custom on Sim Modules.'}
          </Typography>
        </div>
      ) : !allowlistLoaded ? (
        <div className={style.emptyState}>
          <Typography type={TypographyTypes.body}>
            {'Could not load the whitelist for this module.'}
          </Typography>
        </div>
      ) : (
        <>
          <div className={style.searchBox}>
            <SearchBar
              setSearchQuery={setSearchQuery}
              placeholder="Search by partner names and IDs"
              clear={true}
            />
          </div>
          {visiblePartners.length === 0 ? (
            <div className={style.emptyState}>
              <Typography type={TypographyTypes.body}>
                {searchQuery
                  ? 'No partners match your search.'
                  : 'No partners found.'}
              </Typography>
            </div>
          ) : (
            <div className={style.modulesContainer}>
              <table className={style.moduleTable}>
                <thead>
                  <tr>
                    <th className={style.thName}>Partner</th>
                    <th className={style.thSim}>Sim</th>
                    <th className={style.thWhitelisted}>Whitelisted</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePartners.map((partner) => (
                    <tr key={partner.id}>
                      <td className={style.tdName}>{partner.name}</td>
                      <td className={style.tdSim}>
                        {partner.simEnabled ? 'Sim-enabled' : 'Not sim-enabled'}
                      </td>
                      <td className={style.tdWhitelisted}>
                        <Switch
                          checked={partner.whitelisted}
                          onChange={() => togglePartner(partner.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CustomModulePartnersPage
